import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import CasesAPI from '../../../axios/case/CasesApi';
import qs from 'query-string';
import { CaseTagEnum } from '../../../types/enums';
import oneClickPrescribeQueue from '../../../redux/oneClickPrescribeQueue';

const useOneClickPrescribe = (pin) => {
    const dispatch = useDispatch();
    
    const oneClickPrescribeData = useSelector(state => state.oneClickPrescribeQueue.oneClickPrescribeQueue);
    const loading = useSelector(state => state.oneClickPrescribeQueue.oneClickPrescribeQueueLoading);
    const successfulCaseIds = useSelector(state => state.oneClickPrescribeQueue.successfulCaseIds);
    
    const [categories, setCategories] = useState({ selected: null, all: [] });
    const [showFlaggedCases, setShowFlaggedCases] = useState(false);
    const [showLogs, setShowLogs] = useState(false);
    const [categoryCases, setCategoryCases] = useState([]);
    const [submittingCases, setSubmittingCases] = useState(new Set());

    const fetchCases = useCallback(() => {
        dispatch(oneClickPrescribeQueue.actions.getOneClickPrescribeQueue());
    }, [dispatch]);

    const handleCategorySelect = useCallback((categoryId) => {
        if (categories.selected === categoryId) return;
        setCategories(prev => ({ ...prev, selected: categoryId }));
        setShowFlaggedCases(false);
        setShowLogs(false);
    }, [categories.selected]);   

    const handleViewCase = useCallback(async (caseId, requestId) => {
        const path = `/dashboard/physician/caseview/${caseId}`;
        const search = requestId ? qs.stringify({ requestId }) : '';
        window.open(`${path}?${search}&one-click-prescribe=true`, '_blank');
    }, []);

    const handlePrescribe = useCallback(async (_case, overrideFeedback) => {
        if (!_case) return;

        const allMedicationsHaveRxTemplate = _case.requestedMedicines.every(medicine => medicine.rxTemplate);
        const allMedicationsHavePharmacy = _case.requestedMedicines.every(medicine => medicine.rxTemplate?.pharmacyId || _case.pharmacyId);
        
        if (!_case._id || !allMedicationsHaveRxTemplate || !allMedicationsHavePharmacy || !_case.messageTemplate || !(_case.pharmacyId || _case.rxTemplate?.pharmacy)) {
            message.error('Incomplete case data. Cannot prescribe.');
            return;
        }
        
        try {
            setSubmittingCases(prev => new Set(prev).add(_case._id));

            const prescriptions = _case.requestedMedicines.map(medicine => {
                return {
                    ...medicine.rxTemplate.medication,
                    name: medicine.rxTemplate.medication.drugName,
                    directions: medicine.rxTemplate.directions,
                    pharmacyNotes: medicine.rxTemplate.pharmacyNotes,
                    quantity: medicine.rxTemplate.pillCount || medicine.pillCount,
                    refills: medicine.rxTemplate.refills || medicine.refills,
                    daysSupply: medicine.rxTemplate.daysSupply || medicine.daysSupply,
                    pharmacy: { pharmacyId: medicine.rxTemplate.pharmacyId || _case.pharmacyId },
                }
            })

            const requestData = { 
                prescriptions, 
                oneClickPrescribed: true,
                patientExplanation: _case.messageTemplate.message,
                pin 
            };

            // Only add overrideFeedback if it's provided (not empty string)
            if (overrideFeedback) {
                requestData.overrideFeedback = overrideFeedback;
            }

            await CasesAPI.completeConsultation(_case._id, requestData);
                        
            setSubmittingCases(prev => {
                const newSet = new Set(prev);
                newSet.delete(_case._id);
                return newSet;
            });
            
            dispatch(oneClickPrescribeQueue.actions.markOneClickPrescribeCaseSuccessful(_case._id));
        } catch (error) {
            console.error('Prescription error:', error);
            message.error(error.response?.data?.message || 'Failed to prescribe medication. Please try again.');
            
            setSubmittingCases(prev => {
                const newSet = new Set(prev);
                newSet.delete(_case._id);
                return newSet;
            });
        }
    }, [pin, dispatch]);
    
    useEffect(() => {
        if (!oneClickPrescribeData || !Object.keys(oneClickPrescribeData).length) {
            setCategories({ all: [], selected: null });
            return;
        }

        const sortedCategories = Object.entries(oneClickPrescribeData)
            .sort((a, b) => b[1].total - a[1].total)

        // setCategories(prev => ({ ...prev, all: sortedCategories, selected: sortedCategories[0]?.[0] })); 
        setCategories(prev => ({ 
            ...prev, 
            all: sortedCategories, 
            selected: categories.selected || sortedCategories[0]?.[0] 
        })); 
    }, [oneClickPrescribeData]);

    useEffect(() => {
        if (oneClickPrescribeData && categories.selected) {
            const casesByCategory = oneClickPrescribeData[categories.selected]?.cases || [];
                        
            const enhancedCases = casesByCategory.map(_case => {
                const hasBackendError = _case.error || _case.errors?.length > 0;
                const isSubmitting = submittingCases.has(_case._id);
                const wasSuccessful = successfulCaseIds.includes(_case._id);
                                
                return {
                    ..._case,
                    submitting: isSubmitting,
                    success: hasBackendError ? false : (wasSuccessful ? true : undefined)
                };
            });
            
            setCategoryCases(enhancedCases);
        }
    }, [
        oneClickPrescribeData, 
        categories.selected,
        submittingCases,
        successfulCaseIds
    ])

    const handleDismissCase = useCallback(async (_case, isError = false) => {
        if (!_case) return;
        
        const caseId = _case._id;
        
        try {
            if (isError) {
                // For flagged cases, just add 'acknowledged' tag in Redux (no API call, no removal)
                dispatch(oneClickPrescribeQueue.actions.acknowledgeFlaggedCase(caseId));
            } else {
                // For cleared cases, mark as successful and remove from queue
                dispatch(oneClickPrescribeQueue.actions.markOneClickPrescribeCaseSuccessful(caseId));
                
                // Then remove from queue after animation completes (150ms fade + 300ms collapse)
                setTimeout(() => {
                    dispatch(oneClickPrescribeQueue.actions.removeOneClickPrescribeCaseFromQueue(caseId));
                }, 450);
            }
        } catch (error) {
            console.error('Error dismissing case:', error);
            message.error('Failed to dismiss case. Please try again.');
        }
    }, [dispatch]);

    useEffect(() => {
        fetchCases()
    }, [fetchCases])

    return {
        oneClickPrescribeData,
        categoryCases,
        loading,
        categories,
        showFlaggedCases,
        setShowFlaggedCases,
        showLogs,
        setShowLogs,
        setCategories,
        handleCategorySelect,
        handleViewCase,
        handlePrescribe,
        handleDismissCase,
        fetchCases,
    }
}

export default useOneClickPrescribe;